/* sm-custom-configurator.js
   Seth Michael custom order configurator, rendered by
   snippets/sm-custom-configurator.liquid on stock product pages and in the
   /pages/custom studio panels. Each root carries data-sm-cfg and its own
   JSON data; SMCfg.mountAll(scope) mounts the roots inside scope, once each.

   Guest flow: size, fabric, leg, Add to Cart.
   - Fabrics are the real swatch products, listed by sections/sm-fabric-list
     on the collection in shop metafield custom.custom_fabrics_collection.
     A swatch's option named "Grade" ("A", "1000") matches the custom
     product's option named "Grade" ("Grade A", "Grade 1000"). Only fabrics
     whose grade this piece offers are listed, and the fabric sets the grade.
   - Legs are the products in custom.leg_products on the custom product.
   - Required add-ons (custom.required_addons) are priced in the same grade.
   - Add to Cart sends separate lines: the piece, the leg, the fabric, then
     any add-ons. No typed notes. Every line of one build carries the same
     hidden '_build' property (underscore properties are hidden in the cart
     and checkout); sm-onboard copies it into Order Line Items 'Build ID', and
     the furniture PO builder uses it to match each piece with its own fabric
     and leg.
   - A preset can also be handed over directly: a data-sm-cfg-preset
     attribute on the root ({"size","fabric","leg"} as JSON), set by the
     inspiration gallery when it loads this configurator into its own panel
     (sections/sm-build-panel.liquid). That one wins over the address and
     does not scroll the page.
   - A link can open the configurator preset to one build (the inspiration
     gallery's "Price This Build"): ?sm_for=<custom order handle>&sm_size=
     <size>&sm_fabric=<fabric name or swatch handle>&sm_leg=<leg finish>.
     Only the configurator whose custom product matches sm_for takes it. The
     preset only CHOOSES: price, fabric grade, availability and the cart lines
     all come from the same live data as a guest clicking it themselves. A
     choice that cannot be made (fabric not offered in that size, a leg no
     longer offered) is left open with a message, never guessed. */
(function () {
  if (window.SMCfg) return;

  var GRADE_OPTION = 'Grade';
  var GRADE_PREFIX = 'Grade ';
  var MAX_RESULTS  = 8;
  var MAX_SUGGEST  = 3;
  var MAX_PAGES    = 20;
  var ATC_LABEL    = 'Add Custom Order to Cart';
  var fabricLists  = {};
  var uidCounter   = 0;

  var SIZE_MAP = {
    'king':    'King',
    'queen':   'Queen',
    'full xl': 'Full XL',
    'cot':     'Cot'
  };

  function esc(s) {
    return String(s === null || s === undefined ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function normSize(s) {
    return (s || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  // Search words: lower case, accents and punctuation dropped, letters and
  // numbers split apart, leading zeros dropped. "Atlantic 06", "atlantic 6"
  // and "ATLANTIC06" all become ["atlantic", "6"].
  function words(s) {
    var t = String(s || '').toLowerCase();
    if (t.normalize) t = t.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    t = t.replace(/[^a-z0-9]+/g, ' ')
         .replace(/([a-z])([0-9])/g, '$1 $2')
         .replace(/([0-9])([a-z])/g, '$1 $2')
         .trim();
    if (!t) return [];
    return t.split(/\s+/).map(function (w) {
      return /^[0-9]+$/.test(w) ? String(parseInt(w, 10)) : w;
    });
  }

  function isNum(w) { return /^[0-9]+$/.test(w); }

  function lev(a, b) {
    if (a === b) return 0;
    if (!a.length) return b.length;
    if (!b.length) return a.length;
    var prev = [], cur = [], i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      for (j = 1; j <= b.length; j++) {
        cur[j] = Math.min(
          prev[j] + 1,
          cur[j - 1] + 1,
          prev[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1)
        );
      }
      prev = cur;
    }
    return prev[b.length];
  }

  function byTitle(a, b) {
    return a.title.localeCompare(b.title, 'en', { numeric: true, sensitivity: 'base' });
  }

  function withSection(url, sectionId) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return u.pathname + u.search;
  }

  // One shared, cached load of the fabric list per collection, so every
  // configurator on the page (and every studio panel) reuses it.
  function loadFabrics(url) {
    if (fabricLists[url]) return fabricLists[url];
    var list  = [];
    var pages = 0;
    function page(u) {
      pages++;
      return fetch(withSection(u, 'sm-fabric-list'))
        .then(function (r) { if (!r.ok) throw new Error('fabrics ' + r.status); return r.text(); })
        .then(function (html) {
          var doc  = new DOMParser().parseFromString(html, 'text/html');
          var node = doc.querySelector('script[data-sm-fabric-list]');
          if (!node) throw new Error('fabric list missing');
          var data = JSON.parse(node.textContent || '{}');
          (data.items || []).forEach(function (f) {
            if (!f || !f.variantId || !f.grade || !f.title) return;
            var w = words(f.title);
            list.push({
              variantId: f.variantId,
              title:     f.title,
              grade:     String(f.grade),
              image:     f.image || '',
              words:     w,
              key:       w.join(' ')
            });
          });
          if (data.next && pages < MAX_PAGES) return page(data.next);
        });
    }
    fabricLists[url] = page(url)
      .then(function () { list.sort(byTitle); return list; })
      .catch(function (err) { delete fabricLists[url]; throw err; });
    return fabricLists[url];
  }

  // The preset for this configurator, or null. Read once per configurator.
  // 1. Handed over on the root (data-sm-cfg-preset), by the gallery's panel.
  // 2. In the address, when sm_for names this configurator's custom product.
  function readPreset(handle, root) {
    var attr = root && root.getAttribute('data-sm-cfg-preset');
    if (attr) {
      try {
        var o = JSON.parse(attr) || {};
        return { size: o.size || '', fabric: o.fabric || '', leg: o.leg || '', scroll: false };
      } catch (err) { /* fall through to the address */ }
    }
    try {
      var p = new URL(window.location.href).searchParams;
      if (!p.get('sm_for') || p.get('sm_for') !== handle) return null;
      return { size: p.get('sm_size') || '', fabric: p.get('sm_fabric') || '', leg: p.get('sm_leg') || '', scroll: true };
    } catch (err) { return null; }
  }

  function toDollars(cents) { return Math.round(parseFloat(cents) / 100); }
  function formatPrice(n) { return '$' + n.toLocaleString('en-US'); }

  function gradeIndex(options) {
    for (var i = 0; i < (options || []).length; i++) {
      var o = options[i];
      var name = (o && typeof o === 'object') ? o.name : o;
      if (name === GRADE_OPTION) return i;
    }
    return -1;
  }

  function optionValue(v, i) { return v['option' + (i + 1)]; }

  // One id per Add to Cart, shared by every line of that build. Random, never
  // a date or a clock reading: it only has to be unique, not meaningful.
  function newBuildId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID().replace(/-/g, '').slice(0, 12);
    }
    return Math.random().toString(36).slice(2, 8) + Math.random().toString(36).slice(2, 8);
  }

  function priced(v) {
    var p = toDollars(v.price);
    var c = (v.compare_at_price === null || v.compare_at_price === undefined) ? 0 : toDollars(v.compare_at_price);
    return { id: v.id, price: p, compare: c > p ? c : p };
  }

  function mount(root) {
    if (!root || root.getAttribute('data-sm-cfg-mounted') === '1') return;
    root.setAttribute('data-sm-cfg-mounted', '1');

    var CFG = {};
    try {
      var dataEl = root.querySelector('script[data-sm-cfg-data]');
      CFG = JSON.parse((dataEl && dataEl.textContent) || '{}') || {};
    } catch (err) { CFG = {}; }

    var HANDLE = CFG.handle || null;
    if (!HANDLE) return;
    var DEFAULT_SIZE = CFG.defaultSize || null;
    var FABRICS_URL  = CFG.fabrics || '';
    var CONTACT      = CFG.contact || '';
    var LEGS   = (CFG.legs || []).filter(function (l) { return l && l.variantId; });
    var ADDONS = (CFG.addons || []).filter(function (a) { return a && a.handle; });
    var SYNC_PAGE = root.hasAttribute('data-sync-page');
    var PRESET = readPreset(HANDLE, root);
    var UID = 'sm-cfg-' + (++uidCounter);

    var VARIANTS = {};       // size -> grade -> { id, price, compare }
    var SIZES    = [];
    var ADDON_V  = {};       // add-on handle -> grade -> { id, price, compare }
    var FABRICS  = [];
    var productReady = false;
    var fabricsReady = false;
    var busy = false;

    var size = null, fabric = null, leg = null;
    var results = [], active = -1;

    function q(name) { return root.querySelector('[data-sm-cfg-' + name + ']'); }
    var sizesEl   = q('sizes');
    var labelEl   = q('option1-label');
    var priceWrap = q('price-wrap');
    var priceEl   = q('price');
    var fabLabel  = q('fabric-label');
    var fabHint   = q('fabric-hint');
    var comboEl   = q('combo');
    var input     = q('fabric-input');
    var listEl    = q('results');
    var chosenEl  = q('chosen');
    var noteEl    = q('fabric-note');
    var legField  = q('leg-field');
    var legsEl    = q('legs');
    var atcBtn    = q('atc');
    var needsEl   = q('needs');
    var msgEl     = q('message');

    function show(e) { if (e) e.classList.remove('sm-cfg-hidden'); }
    function hide(e) { if (e) e.classList.add('sm-cfg-hidden'); }

    if (input) {
      input.id = UID + '-fabric';
      if (fabLabel) fabLabel.setAttribute('for', input.id);
      if (listEl) {
        listEl.id = UID + '-list';
        input.setAttribute('aria-controls', listEl.id);
      }
    }

    function showMessage(type, text) {
      if (!msgEl) return;
      msgEl.className = 'sm-cfg-message sm-cfg-' + type;
      msgEl.textContent = text;
    }
    function hideMessage() { hide(msgEl); }

    function setNote(text, isError) {
      if (!noteEl) return;
      noteEl.textContent = text || '';
      noteEl.classList.toggle('sm-cfg-note-error', !!isError);
      if (text) show(noteEl); else hide(noteEl);
    }

    function contactLine() {
      return CONTACT ? ', or text or call us at ' + CONTACT : '';
    }

    function gradeOf(f) { return GRADE_PREFIX + f.grade; }

    // Price of this piece in one size and grade, with any required add-ons
    // in the same grade. null when that combination does not exist.
    function quote(s, g) {
      var v = VARIANTS[s] && VARIANTS[s][g];
      if (!v) return null;
      var out = { id: v.id, price: v.price, compare: v.compare, addons: [] };
      for (var i = 0; i < ADDONS.length; i++) {
        var a  = ADDONS[i];
        var av = ADDON_V[a.handle] && ADDON_V[a.handle][g];
        if (!av) return null;
        out.price   += av.price;
        out.compare += av.compare;
        out.addons.push({ id: av.id, name: a.name || a.handle });
      }
      return out;
    }

    function fabricOk(f) { return !!(size && quote(size, gradeOf(f))); }

    function resolveSize(raw) {
      var n = normSize(raw);
      if (!n) return null;
      for (var i = 0; i < SIZES.length; i++) {
        if (normSize(SIZES[i]) === n) return SIZES[i];
      }
      var alias = SIZE_MAP[n];
      if (alias) {
        var an = normSize(alias);
        for (var j = 0; j < SIZES.length; j++) {
          if (normSize(SIZES[j]) === an) return SIZES[j];
        }
      }
      return null;
    }

    /* ---------- data ---------- */

    function loadProduct() {
      return fetch('/products/' + encodeURIComponent(HANDLE) + '.js')
        .then(function (r) { if (!r.ok) throw new Error('product ' + r.status); return r.json(); })
        .then(function (p) {
          var gi = gradeIndex(p.options);
          if (gi === -1) throw new Error('no Grade option');
          var si = gi === 0 ? 1 : 0;
          var opt = (p.options || [])[si];
          var order = (opt && opt.values) ? opt.values.slice() : [];
          if (labelEl && opt && opt.name) labelEl.textContent = 'Select ' + opt.name;
          (p.variants || []).forEach(function (v) {
            if (v.available === false) return;
            var s = optionValue(v, si);
            var g = optionValue(v, gi);
            if (!VARIANTS[s]) { VARIANTS[s] = {}; SIZES.push(s); }
            VARIANTS[s][g] = priced(v);
          });
          SIZES.sort(function (a, b) {
            var ai = order.indexOf(a), bi = order.indexOf(b);
            if (ai === -1) ai = 9999;
            if (bi === -1) bi = 9999;
            return ai - bi;
          });
        });
    }

    function loadAddons() {
      return Promise.all(ADDONS.map(function (a) {
        return fetch('/products/' + encodeURIComponent(a.handle) + '.js')
          .then(function (r) { if (!r.ok) throw new Error('add-on ' + r.status); return r.json(); })
          .then(function (p) {
            var gi = gradeIndex(p.options);
            var byGrade = {};
            (p.variants || []).forEach(function (v) {
              if (gi === -1 || v.available === false) return;
              var g = optionValue(v, gi);
              if (!byGrade[g]) byGrade[g] = priced(v);
            });
            ADDON_V[a.handle] = byGrade;
          });
      }));
    }

    /* ---------- size ---------- */

    function renderSizes() {
      if (!sizesEl) return;
      sizesEl.innerHTML = '';
      SIZES.forEach(function (s) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sm-cfg-pill';
        b.setAttribute('data-size', s);
        b.setAttribute('aria-pressed', 'false');
        b.textContent = s;
        b.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setSize(s);
        });
        sizesEl.appendChild(b);
      });
    }

    function setSize(s) {
      if (!s || !VARIANTS[s]) return;
      size = s;
      Array.prototype.forEach.call(sizesEl ? sizesEl.querySelectorAll('.sm-cfg-pill') : [], function (p) {
        var on = p.getAttribute('data-size') === s;
        p.classList.toggle('sm-cfg-pill-active', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      hideMessage();
      if (fabric && !fabricOk(fabric)) {
        var name = fabric.title;
        clearFabric();
        showMessage('error', name + ' is not offered in ' + s + '. Please choose another fabric.');
        show(msgEl);
      }
      if (input && !fabric && input.value.trim() && listEl && !listEl.classList.contains('sm-cfg-hidden')) runSearch();
      renderPrice();
      refresh();
    }

    /* ---------- price ---------- */

    function renderPrice() {
      if (!priceEl || !size || !productReady || !fabricsReady) { hide(priceWrap); return; }
      var qt = null;
      var starting = !fabric;
      if (fabric) {
        qt = quote(size, gradeOf(fabric));
      } else {
        var seen = {};
        FABRICS.forEach(function (f) {
          var g = gradeOf(f);
          if (seen[g]) return;
          seen[g] = 1;
          var c = quote(size, g);
          if (c && (!qt || c.price < qt.price)) qt = c;
        });
      }
      if (!qt) { hide(priceWrap); return; }

      var onSale = qt.compare > qt.price;
      var amount =
        (onSale ? '<span class="sm-cfg-price-compare">' + formatPrice(qt.compare) + '</span>' : '') +
        '<span class="sm-cfg-price-amount">' + formatPrice(qt.price) + '</span>' +
        (onSale ? '<span class="sm-cfg-savings">You save ' + formatPrice(qt.compare - qt.price) + '</span>' : '');

      var html = '<span class="sm-cfg-price-size">' + esc(size) + '</span> &middot; ';
      if (starting) {
        html += 'Starting at ' + amount;
      } else {
        html += '<span class="sm-cfg-price-grade">' + esc(gradeOf(fabric)) + '</span>' +
                ' &middot; Made in Europe for ' + amount;
      }
      priceEl.innerHTML = html;

      if (qt.addons.length) {
        var inc = document.createElement('span');
        inc.className = 'sm-cfg-includes';
        inc.textContent = 'Includes ' + qt.addons.map(function (a) { return a.name; }).join(' and ');
        priceEl.appendChild(inc);
      }
      if (starting) {
        var hint = document.createElement('span');
        hint.className = 'sm-cfg-includes';
        hint.textContent = 'Made in Europe. The fabric you choose sets the final price.';
        priceEl.appendChild(hint);
      }

      if (priceWrap) priceWrap.classList.toggle('sm-cfg-price-wrap-sale', onSale);
      show(priceWrap);
    }

    /* ---------- fabric search ---------- */

    function search(text) {
      var qw = words(text);
      if (!qw.length) return { items: [], more: 0, suggest: false };
      var pool = FABRICS.filter(fabricOk);
      var qkey = qw.join(' ');

      var hits = [];
      pool.forEach(function (f) {
        var all = qw.every(function (w) {
          return f.words.some(function (t) { return t.indexOf(w) === 0; });
        });
        if (all) hits.push({ f: f, rank: f.key === qkey ? 0 : (f.key.indexOf(qkey) === 0 ? 1 : 2) });
      });
      if (hits.length) {
        hits.sort(function (a, b) { return (a.rank - b.rank) || byTitle(a.f, b.f); });
        return {
          items:   hits.slice(0, MAX_RESULTS).map(function (h) { return h.f; }),
          more:    Math.max(0, hits.length - MAX_RESULTS),
          suggest: false
        };
      }

      // No direct match: suggest close spellings. The fabric name must be
      // close, and a typed number must match the fabric number exactly.
      var scored = [];
      pool.forEach(function (f) {
        var matched = 0, dist = 0, numHit = false, nameHit = false;
        qw.forEach(function (w) {
          var best = 99, bestAt = -1;
          f.words.forEach(function (t, i) {
            var d;
            if (isNum(w) || isNum(t)) {
              d = (isNum(w) && isNum(t) && w === t) ? 0 : 99;
            } else {
              d = lev(w, t);
              if (w.length < t.length) d = Math.min(d, lev(w, t.slice(0, w.length)));
            }
            if (d < best) { best = d; bestAt = i; }
          });
          var tol = isNum(w) ? 0 : (w.length <= 3 ? 0 : (w.length <= 6 ? 1 : 2));
          if (best <= tol) {
            matched++;
            dist += best;
            if (isNum(w)) numHit = true;
            if (bestAt === 0) nameHit = true;
          }
        });
        if (matched && nameHit) scored.push({ f: f, matched: matched, dist: dist, num: numHit ? 1 : 0 });
      });
      // When a typed number matches some fabric, offer only those.
      if (scored.some(function (s) { return s.num; })) {
        scored = scored.filter(function (s) { return s.num; });
      }
      scored.sort(function (a, b) {
        return (b.matched - a.matched) || (b.num - a.num) || (a.dist - b.dist) || byTitle(a.f, b.f);
      });
      var limit = qw.some(isNum) ? MAX_SUGGEST : MAX_RESULTS;
      return { items: scored.slice(0, limit).map(function (s) { return s.f; }), more: 0, suggest: true };
    }

    function exactMatch(text) {
      var key = words(text).join(' ');
      if (!key) return null;
      var found = FABRICS.filter(function (f) { return f.key === key && fabricOk(f); });
      return found.length === 1 ? found[0] : null;
    }

    function swatchImg(src, cls) {
      if (!src) {
        var ph = document.createElement('span');
        ph.className = cls + ' sm-cfg-swatch-empty';
        return ph;
      }
      var img = document.createElement('img');
      img.className = cls;
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 44;
      img.height = 44;
      return img;
    }

    function listNote(text) {
      var n = document.createElement('div');
      n.className = 'sm-cfg-results-note';
      n.setAttribute('role', 'presentation');
      n.textContent = text;
      listEl.appendChild(n);
    }

    function openList() {
      show(listEl);
      if (input) input.setAttribute('aria-expanded', 'true');
    }

    function closeList() {
      hide(listEl);
      active = -1;
      if (input) {
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      }
    }

    function updateActive() {
      Array.prototype.forEach.call(listEl.querySelectorAll('.sm-cfg-option'), function (o, i) {
        var on = i === active;
        o.classList.toggle('is-active', on);
        o.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) {
          input.setAttribute('aria-activedescendant', o.id);
          // Keep the highlighted row in view without scrolling the page.
          if (o.offsetTop < listEl.scrollTop) listEl.scrollTop = o.offsetTop;
          else if (o.offsetTop + o.offsetHeight > listEl.scrollTop + listEl.clientHeight) {
            listEl.scrollTop = o.offsetTop + o.offsetHeight - listEl.clientHeight;
          }
        }
      });
      if (active < 0 && input) input.removeAttribute('aria-activedescendant');
    }

    function runSearch() {
      if (!listEl || !input || !fabricsReady) return;
      var text = input.value.trim();
      if (!text) { results = []; closeList(); listEl.innerHTML = ''; return; }
      var res = search(text);
      results = res.items;
      active = results.length ? 0 : -1;
      listEl.innerHTML = '';

      if (res.suggest && results.length) listNote('No exact match. Did you mean:');
      if (!results.length) {
        listNote('We could not find that fabric. Check the name and number on your swatch' + contactLine() + '.');
      }
      results.forEach(function (f, i) {
        var o = document.createElement('div');
        o.className = 'sm-cfg-option';
        o.id = UID + '-opt-' + i;
        o.setAttribute('role', 'option');
        o.setAttribute('aria-selected', 'false');
        o.appendChild(swatchImg(f.image, 'sm-cfg-swatch'));
        var name = document.createElement('span');
        name.className = 'sm-cfg-option-name';
        name.textContent = f.title;
        var grade = document.createElement('span');
        grade.className = 'sm-cfg-option-grade';
        grade.textContent = gradeOf(f);
        o.appendChild(name);
        o.appendChild(grade);
        o.addEventListener('mousedown', function (e) { e.preventDefault(); });
        o.addEventListener('click', function () { chooseFabric(f); });
        listEl.appendChild(o);
      });
      if (res.more) listNote('Keep typing to narrow down ' + res.more + ' more.');
      openList();
      updateActive();
    }

    function chooseFabric(f) {
      if (!f || !fabricOk(f)) return;
      fabric = f;
      if (input) input.value = f.title;
      closeList();
      hideMessage();

      chosenEl.innerHTML = '';
      chosenEl.appendChild(swatchImg(f.image, 'sm-cfg-swatch sm-cfg-swatch-lg'));
      var text = document.createElement('span');
      text.className = 'sm-cfg-chosen-text';
      var n = document.createElement('span');
      n.className = 'sm-cfg-chosen-name';
      n.textContent = f.title;
      var g = document.createElement('span');
      g.className = 'sm-cfg-chosen-grade';
      g.textContent = gradeOf(f);
      text.appendChild(n);
      text.appendChild(g);
      var change = document.createElement('button');
      change.type = 'button';
      change.className = 'sm-cfg-chosen-change';
      change.textContent = 'Change';
      change.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        clearFabric();
        if (input) { input.value = ''; input.focus(); }
      });
      chosenEl.appendChild(text);
      chosenEl.appendChild(change);

      hide(comboEl);
      show(chosenEl);
      if (fabHint) fabHint.textContent = gradeOf(f);
      renderPrice();
      refresh();
    }

    function clearFabric() {
      fabric = null;
      hide(chosenEl);
      if (chosenEl) chosenEl.innerHTML = '';
      show(comboEl);
      if (fabHint) fabHint.textContent = '';
      renderPrice();
      refresh();
    }

    function wireFabric() {
      if (!input) return;
      input.addEventListener('input', function () {
        if (fabric) clearFabric();
        hideMessage();
        runSearch();
      });
      input.addEventListener('focus', function () {
        if (input.value.trim()) runSearch();
      });
      input.addEventListener('blur', function () {
        setTimeout(function () {
          if (document.activeElement === input) return;
          closeList();
          if (!fabric) {
            var f = exactMatch(input.value);
            if (f) chooseFabric(f);
          }
        }, 150);
      });
      input.addEventListener('keydown', function (e) {
        var open = listEl && !listEl.classList.contains('sm-cfg-hidden');
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          if (!results.length) return;
          e.preventDefault();
          if (!open) { openList(); }
          var step = e.key === 'ArrowDown' ? 1 : -1;
          active = (active + step + results.length) % results.length;
          updateActive();
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (open && active >= 0 && results[active]) { chooseFabric(results[active]); return; }
          var f = exactMatch(input.value);
          if (f) chooseFabric(f);
        } else if (e.key === 'Escape') {
          closeList();
        }
      });
    }

    /* ---------- legs ---------- */

    function renderLegs() {
      if (!LEGS.length || !legsEl) { hide(legField); return; }
      legsEl.innerHTML = '';
      LEGS.forEach(function (l) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sm-cfg-pill sm-cfg-leg-pill';
        b.setAttribute('data-leg-id', String(l.variantId));
        b.setAttribute('aria-pressed', 'false');
        if (l.image) {
          var img = document.createElement('img');
          img.className = 'sm-cfg-leg-img';
          img.src = l.image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 26;
          img.height = 26;
          b.appendChild(img);
        }
        var t = document.createElement('span');
        t.textContent = l.title;
        b.appendChild(t);
        b.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          setLeg(l);
        });
        legsEl.appendChild(b);
      });
      show(legField);
      if (LEGS.length === 1) setLeg(LEGS[0]);
    }

    function setLeg(l) {
      leg = l;
      Array.prototype.forEach.call(legsEl.querySelectorAll('.sm-cfg-pill'), function (p) {
        var on = p.getAttribute('data-leg-id') === String(l.variantId);
        p.classList.toggle('sm-cfg-pill-active', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      hideMessage();
      refresh();
    }

    /* ---------- add to cart ---------- */

    function ready() {
      return !!(productReady && fabricsReady && size && fabric &&
        quote(size, gradeOf(fabric)) && (!LEGS.length || leg));
    }

    function refresh() {
      var need = [];
      if (!fabric) need.push('a fabric');
      if (LEGS.length && !leg) need.push('a leg');
      if (atcBtn) atcBtn.disabled = busy || !ready();
      if (needsEl) {
        needsEl.textContent = (productReady && fabricsReady && need.length)
          ? 'Choose ' + need.join(' and ') + ' to add this to your cart.'
          : '';
      }
    }

    function openCartDrawer(cartDrawer) {
      if (!cartDrawer) return;

      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      } else {
        cartDrawer.classList.add('active');
        cartDrawer.removeAttribute('aria-hidden');
        document.documentElement.classList.add('overflow-hidden');
        var inner = cartDrawer.querySelector('.drawer__inner');
        if (inner) inner.setAttribute('tabindex', '-1');
      }

      if (!cartDrawer.dataset.smCleanupBound) {
        cartDrawer.dataset.smCleanupBound = '1';
        new MutationObserver(function () {
          if (!cartDrawer.classList.contains('active')) {
            document.documentElement.classList.remove('overflow-hidden');
          }
        }).observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
      }
    }

    function refreshDrawer(piece) {
      fetch('/?sections=cart-drawer')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          var html = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');

          var newItems = html.querySelector('cart-drawer-items');
          var oldItems = document.querySelector('cart-drawer-items');
          if (newItems && oldItems) oldItems.replaceWith(newItems);

          var emptyDiv = document.querySelector('.drawer__inner-empty');
          if (emptyDiv) emptyDiv.remove();

          var cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer) cartDrawer.classList.remove('is-empty');

          var checkoutBtn = document.getElementById('CartDrawer-Checkout');
          if (checkoutBtn) checkoutBtn.disabled = false;

          openCartDrawer(cartDrawer);

          // sleeper-sofa-upsells listens for this and reads the piece's
          // option named "Size". Always send the piece, never another line.
          if (piece) {
            setTimeout(function () {
              document.dispatchEvent(new CustomEvent('ssu:custom:added', {
                detail: { handle: piece.handle, options: piece.options_with_values }
              }));
            }, 500);
          }
        });
    }

    function doneAdding() {
      busy = false;
      if (atcBtn) atcBtn.textContent = ATC_LABEL;
      refresh();
    }

    function wireATC() {
      if (!atcBtn) return;
      atcBtn.addEventListener('click', function (e) {
        e.stopImmediatePropagation();
        e.preventDefault();
        if (busy || !ready()) { refresh(); return; }
        var qt = quote(size, gradeOf(fabric));

        // Cart lines, in the order we build custom orders by hand:
        // piece, leg, fabric, then required add-ons in the same grade.
        // Every line carries the same hidden '_build' id. It is what lets the
        // furniture PO match this piece with its own fabric and leg when an
        // order holds two builds (or a Flex armless with its arms), and it keeps
        // two builds, or a build and free samples, from merging into one line.
        var tag = { _build: newBuildId() };
        var items = [{ id: qt.id, quantity: 1, properties: tag }];
        if (leg) items.push({ id: leg.variantId, quantity: 1, properties: tag });
        items.push({ id: fabric.variantId, quantity: 1, properties: tag });
        qt.addons.forEach(function (a) { items.push({ id: a.id, quantity: 1, properties: tag }); });

        busy = true;
        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding...';
        hideMessage();

        fetch('/cart/add.js', {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data.status && data.status !== 200) {
            showMessage('error', data.description || data.message || 'Could not add to cart.');
            show(msgEl);
            doneAdding();
            return;
          }
          showMessage('success', 'Your custom order has been added to your cart!');
          show(msgEl);
          doneAdding();
          var added = data.items || [];
          var piece = added.filter(function (i) { return i.variant_id === qt.id; })[0] || added[0] || null;
          setTimeout(function () { refreshDrawer(piece); }, 300);
        })
        .catch(function () {
          showMessage('error', 'Something went wrong. Please try again.');
          show(msgEl);
          doneAdding();
        });
      });
    }

    /* ---------- product page size sync ---------- */

    function getSelectedSizeFromPage() {
      var checkedInput = document.querySelector('.variant-pills__input:checked, input.product-form__radio:checked');
      if (checkedInput) {
        var label = document.querySelector('label[for="' + checkedInput.id + '"]');
        if (label) {
          var byLabel = resolveSize(label.textContent);
          if (byLabel) return byLabel;
        }
        var byVal = resolveSize(checkedInput.value);
        if (byVal) return byVal;
      }
      var select = document.querySelector('select[data-option-index="0"], select[id*="option-0"], select[name*="Size"]');
      if (select && select.value) return resolveSize(select.value);
      var anyChecked = document.querySelector('input[type="radio"]:checked');
      if (anyChecked) return resolveSize(anyChecked.value);
      return null;
    }

    function watchPageSizeChanges() {
      document.querySelectorAll('.variant-pills__input, input.product-form__radio, input[type="radio"]').forEach(function (el) {
        if (root.contains(el)) return;
        el.addEventListener('change', function () {
          var s = getSelectedSizeFromPage();
          if (s) setSize(s);
        });
      });
      document.querySelectorAll('select[data-option-index="0"], select[id*="option-0"], select[name*="Size"]').forEach(function (sel) {
        if (root.contains(sel)) return;
        sel.addEventListener('change', function () {
          var s = resolveSize(sel.value);
          if (s) setSize(s);
        });
      });
    }

    /* ---------- preset from a link ---------- */

    // Fabric by its words, so "Megan 12", "megan-12" (the swatch handle) and
    // "Megan 012" all find the same swatch. Leg by finish: an exact finish
    // first ("Black Wood" never takes "Black Metal"), else the one leg whose
    // title holds every word given.
    function applyPreset() {
      var missing = [];
      if (PRESET.size && !resolveSize(PRESET.size)) missing.push('the size ' + PRESET.size);
      if (PRESET.fabric) {
        var key = words(PRESET.fabric).join(' ');
        var f = FABRICS.filter(function (x) { return x.key === key; })[0];
        if (f && fabricOk(f)) chooseFabric(f);
        else missing.push(f ? f.title + ' in ' + size : 'that fabric');
      }
      if (PRESET.leg && LEGS.length) {
        var lw = words(PRESET.leg);
        var finish = function (x) { return words(x.title).filter(function (w) { return w !== 'legs' && w !== 'leg'; }).join(' '); };
        var exact = LEGS.filter(function (x) { return finish(x) === lw.join(' '); });
        var loose = LEGS.filter(function (x) { var tw = words(x.title); return lw.every(function (w) { return tw.indexOf(w) > -1; }); });
        var l = exact[0] || (loose.length === 1 ? loose[0] : null);
        if (l) setLeg(l); else missing.push(PRESET.leg + ' legs');
      }
      if (missing.length) {
        showMessage('error', 'We could not set ' + missing.join(' and ') + ' for this piece online. Please choose below' + contactLine() + '.');
        show(msgEl);
      }
      if (PRESET.scroll) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    /* ---------- start ---------- */

    function init() {
      wireFabric();
      wireATC();
      renderLegs();
      refresh();

      var fabricsLoad = FABRICS_URL
        ? loadFabrics(FABRICS_URL)
        : Promise.reject(new Error('no fabric collection set'));
      fabricsLoad.catch(function () {});

      loadProduct()
        .then(loadAddons)
        .then(function () {
          productReady = true;
          renderSizes();
          var start =
            (PRESET && PRESET.size && resolveSize(PRESET.size)) ||
            (DEFAULT_SIZE && resolveSize(DEFAULT_SIZE)) ||
            (SYNC_PAGE && getSelectedSizeFromPage()) ||
            SIZES[0] ||
            null;
          if (start) setSize(start);
          if (SYNC_PAGE) watchPageSizeChanges();

          return fabricsLoad.then(function (list) {
            FABRICS = list;
            fabricsReady = true;
            if (input) input.disabled = false;
            if (FABRICS.filter(fabricOk).length) {
              setNote('');
            } else {
              setNote('Fabrics for this piece are not available online right now. Please contact us' + contactLine() + '.', true);
            }
            renderPrice();
            refresh();
            if (PRESET) applyPreset();
          }, function () {
            setNote('Our fabric list did not load. Please refresh the page' + contactLine() + '.', true);
            refresh();
          });
        })
        .catch(function () {
          setNote('');
          showMessage('error', 'Sorry, this piece did not load. Please refresh the page' + contactLine() + '.');
          show(msgEl);
          refresh();
        });
    }

    init();
  }

  function mountAll(scope) {
    var list = (scope || document).querySelectorAll('[data-sm-cfg]');
    Array.prototype.forEach.call(list, mount);
  }

  window.SMCfg = { mount: mount, mountAll: mountAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mountAll(document); });
  } else {
    mountAll(document);
  }

})();
